import { useEffect, useState } from 'react';
import { getCarPositions, getCarPositionsStreamUrl } from './api';

export default function useLiveMap(raceId) {
  const [snapshot, setSnapshot] = useState({ carPositions: {}, source: 'connecting', stale: true });
  useEffect(() => {
    setSnapshot({ carPositions: {}, source: 'connecting', stale: true });
    if (!raceId) return undefined;
    let disposed = false;
    let lastMessage = 0;
    let polling = false;
    let nextPoll = 0;
    let controller;
    let stream;
    const accept = data => {
      if (disposed) return;
      setSnapshot(previous => {
        const positions = { ...previous.carPositions };
        let changed = false;
        for (const [acronym, point] of Object.entries(data.carPositions || {})) {
          if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) continue;
          if (positions[acronym] && Date.parse(positions[acronym].date) >= Date.parse(point.date)) continue;
          if (!Number.isFinite(Date.parse(point.date))) continue;
          positions[acronym] = point;
          changed = true;
        }
        return { ...data, carPositions: changed ? positions : previous.carPositions };
      });
    };
    if (typeof EventSource !== 'undefined') {
      stream = new EventSource(getCarPositionsStreamUrl(raceId));
      stream.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          lastMessage = Date.now();
          accept(data);
        } catch { /* Ignore malformed frames; the watchdog will start fallback. */ }
      };
      stream.onerror = () => { lastMessage = 0; };
    }
    // EventSource reconnects automatically. Poll our shared cache only while
    // the browser stream is unavailable, never with overlapping requests.
    const fallback = async () => {
      if (disposed || polling || Date.now() < nextPoll || Date.now() - lastMessage < 5000) return;
      polling = true;
      controller = new AbortController();
      try {
        const data = await getCarPositions(raceId, controller.signal);
        if (Date.now() - lastMessage >= 5000) accept(data);
      } catch {
        if (!disposed) setSnapshot(previous => ({ ...previous, stale: true }));
      } finally { polling = false; nextPoll = Date.now() + 1800; }
    };
    // Allow the SSE connection time to open before requesting the fallback.
    nextPoll = Date.now() + 5000;
    const watchdog = setInterval(fallback, 1000);
    return () => { disposed = true; clearInterval(watchdog); stream?.close(); controller?.abort(); };
  }, [raceId]);
  return snapshot;
}
